import type { ReactNode } from 'react';
import { PLAN_PRICES, getPlanDisplayName } from '../lib/plans';

export type PricingPlan = keyof typeof PLAN_PRICES;

const PLANS: Array<{ plan: PricingPlan; prompts: number; team: ReactNode }> = [
  { plan: 'starter', prompts: 20, team: <>Up to <strong>2</strong> team members</> },
  { plan: 'growth', prompts: 75, team: <>Up to <strong>10</strong> team members</> },
  { plan: 'scale', prompts: 300, team: <><strong>Unlimited</strong> team members</> },
];

const CheckmarkIcon = () => (
  <div className="checkmark-svg w-embed">
    <svg width="16" height="12" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.5087 0.765625L6.93892 11.7051L2.10651 7.27186L0.59375 8.9177L6.19655 14.0582L6.96694 14.7656L7.72332 14.0513L20.0495 2.39744L18.5087 0.765625Z" fill="currentColor"></path>
    </svg>
  </div>
);

export function BillingToggle({ isYearly, onChange }: { isYearly: boolean; onChange: (isYearly: boolean) => void }) {
  return (
    <div className="toggle-container">
      <div className="monthly-text" style={{ opacity: isYearly ? 0.35 : 1 }}>Monthly</div>
      <div className="toggle-spacer"></div>
      <div
        className={`toggle is-pricing ${isYearly ? 'is-active' : ''}`}
        onClick={() => onChange(!isYearly)}
      >
        <div className="toggle-ball is-pricing-ball"></div>
      </div>
      <div className="toggle-spacer"></div>
      <div className="yearly-text" style={{ opacity: isYearly ? 1 : 0.35 }}>Yearly</div>
    </div>
  );
}

// The plan cards; each page supplies its own button.
export function PricingCards({ isYearly, renderButton }: { isYearly: boolean; renderButton: (plan: PricingPlan) => ReactNode }) {
  return (
    <div className="pricing-flex" style={{ display: 'flex' }}>
      {PLANS.map(({ plan, prompts, team }) => (
        <div className="pricing-box" key={plan}>
          <div className="pricing-box-info">
            <div className="text-size-large text-weight-bold">{getPlanDisplayName(plan)}</div>
            <div className="spacer-tiny"></div>
            <div className="text-size-medium text-colour-dark-grey">{PLAN_PRICES[plan][isYearly ? 'yearly' : 'monthly']}</div>
            <div className="spacer-xtiny"></div>
            <div className="line-divider"></div>
            <div className="spacer-xtiny"></div>
            <div className="plan-checkbox">
              <CheckmarkIcon />
              <div className="text-size-regular">Monitor up to <strong className="gradient-text-bg">{prompts}</strong> prompts</div>
            </div>
            <div className="plan-checkbox">
              <CheckmarkIcon />
              <div className="text-size-regular">Analytics and insights</div>
            </div>
            <div className="plan-checkbox">
              <CheckmarkIcon />
              <div className="text-size-regular"><strong>Daily</strong> tracking</div>
            </div>
            <div className="plan-checkbox">
              <CheckmarkIcon />
              <div className="text-size-regular">Track visibility across Google, ChatGPT, Claude, and Perplexity</div>
            </div>
            <div className="plan-checkbox">
              <CheckmarkIcon />
              <div className="text-size-regular">{team}</div>
            </div>
            <div className="pricing-cta-holder">
              {renderButton(plan)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
